// Treemap tile. V-shape-native: resolves the `:label`/`:value`
// `ColumnRef`s (and the optional `:group` ref) to columns, shapes them
// into a recharts Treemap data array — one rectangle per row, nested one
// level under the `:group` value when present — and renders with the
// recharts `<Treemap>`. Tiles are coloured by category through the shared
// `categoryColorScale` helper (with `:colors` overrides), so the same
// category always renders the same palette token across renders.

import { type ComponentProps, useId } from 'react';
import { ResponsiveContainer, Treemap } from 'recharts';

import { formatNumber, numberHints } from '#core/components/dashboard/number-format.ts';
import { isDrawableTreemapValue } from '#core/lib/expr/ops/dashboard/validate.ts';
import type { TreemapSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { cellScalar } from '#core/lib/expr/value.ts';
import { categoryColorScale } from '#core/lib/formatters/categoryColors.ts';
import { type MantineColor, mantineColorVar } from '#core/lib/formatters/colors.ts';
import { csvFromRows } from '#core/lib/transforms/csv-from-rows.ts';
import { slugify } from '#core/lib/utils/slugify.ts';

import { TileFrame } from './TileFrame.tsx';
import classes from './TreemapPanel.module.css';
import { useResolvedColumns } from './useResolvedColumns.ts';

/** Synthetic bucket the `:max-tiles` tail collapses into. */
const OTHER = 'Other';

/** One leaf rectangle in the recharts treemap data array. */
interface TreemapLeaf {
  readonly name: string;
  readonly size: number;
  /** Category key driving the shared colour scale (the group when nested, else the label). */
  readonly category: string;
}

/** One parent rectangle wrapping a level of leaves (only when `:group` is set). */
interface TreemapBranch {
  readonly name: string;
  readonly category: string;
  readonly children: readonly TreemapLeaf[];
}

type TreemapNode = TreemapLeaf | TreemapBranch;

/**
 * Collapse a value-sorted leaf list past the top-N into a single `Other`
 * bucket. Returns the list unchanged when no cap applies or nothing
 * overflows — the `Other` bucket only appears when there is a genuine tail.
 */
function capLeaves(leaves: readonly TreemapLeaf[], maxTiles: number | undefined): readonly TreemapLeaf[] {
  if (maxTiles === undefined || leaves.length <= maxTiles) {
    return leaves;
  }
  const kept = leaves.slice(0, maxTiles);
  const tail = leaves.slice(maxTiles);
  const otherSize = tail.reduce((sum, leaf) => sum + leaf.size, 0);
  return [...kept, { name: OTHER, size: otherSize, category: OTHER }];
}

/**
 * recharts hands the content renderer a flattened node, painting every node
 * including the root and (for grouped data) the parent branches. A leaf sits at
 * `leafDepth` — depth 1 for a flat treemap, depth 2 for a grouped one — which
 * the panel knows from `:group` and passes down. We colour the leaves and leave
 * the shallower root/branch frames transparent so a grouping boundary reads as
 * chrome. (`children` is avoided as a prop name: recharts injects a child-node
 * array for parents, which React would treat as renderable children.)
 */
interface TileContentProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly name?: string;
  readonly category?: string;
  readonly value?: number;
  readonly depth?: number;
  readonly leafDepth: number;
  readonly fmt: (n: number) => string;
  readonly scale: Map<string, MantineColor>;
}

function TreemapTile({ x, y, width, height, name, category, value, depth, leafDepth, fmt, scale }: TileContentProps) {
  const w = width ?? 0;
  const h = height ?? 0;
  const px = x ?? 0;
  const py = y ?? 0;
  // A leaf sits at `leafDepth` (1 flat, 2 grouped). The shallower root and any
  // group-parent frames render as a transparent outline so the grouping
  // boundary is visible without a fill colour.
  const isLeaf = depth === leafDepth;
  const fill = isLeaf ? mantineColorVar(scale.get(category ?? '') ?? 'blue') : 'transparent';
  const showLabel = isLeaf && w > 48 && h > 24 && name !== undefined;
  return (
    <g>
      <rect
        className={classes.tile}
        fill={fill}
        height={h}
        stroke="var(--mantine-color-body)"
        strokeWidth={isLeaf ? 1 : 5}
        width={w}
        x={px}
        y={py}
      />
      {showLabel && (
        <text className={classes.label} x={px + 6} y={py + 16}>
          {name}
        </text>
      )}
      {showLabel && value !== undefined && h > 40 && (
        <text className={classes.value} x={px + 6} y={py + 32}>
          {fmt(value)}
        </text>
      )}
    </g>
  );
}

export function TreemapPanel({ spec }: { spec: TreemapSpec }) {
  const tileId = useId();
  // Stable, positionally-indexed ref list: label, value, then group (if any).
  const refs = [spec.label, spec.value, ...(spec.group === undefined ? [] : [spec.group])];
  const cols = useResolvedColumns(refs);
  const labels = cols[0] ?? [];
  const values = cols[1] ?? [];
  const groups = spec.group === undefined ? undefined : (cols[2] ?? []);

  // One leaf per row that passes the SSOT leaf predicate shared with
  // `validateDashboardSpec` (a finite numeric value), so a treemap that
  // validated draws exactly these rows. The category key drives the
  // colour scale: the group value when nested (so a whole parent shares
  // one hue), else the label itself (each tile its own colour).
  const rawLeaves = labels.flatMap((labelCell, r): TreemapLeaf[] => {
    const value = cellScalar(values[r]);
    if (!isDrawableTreemapValue(value)) {
      return [];
    }
    const label = cellScalar(labelCell);
    const name = label === null ? '' : String(label);
    const group = groups === undefined ? null : cellScalar(groups[r]);
    const category = group === null ? name : String(group);
    return [{ name, size: value, category }];
  });

  // Sort by value descending so the `:max-tiles` tail collapse keeps the
  // largest tiles and the recharts squarified layout reads big→small.
  const sorted = [...rawLeaves].sort((a, b) => b.size - a.size);

  // Flat treemap: a single capped, coloured-by-label leaf list.
  // Nested treemap: group leaves under their parent (insertion order of first
  // appearance), capping the parent count, leaves coloured by group.
  const flat = spec.group === undefined;
  const cappedFlat = capLeaves(sorted, spec.maxTiles);

  const branches = (() => {
    if (flat) {
      return [];
    }
    const byGroup = sorted.reduce<Map<string, TreemapLeaf[]>>((acc, leaf) => {
      const bucket = acc.get(leaf.category) ?? [];
      acc.set(leaf.category, [...bucket, leaf]);
      return acc;
    }, new Map());
    const ordered = [...byGroup.entries()].sort(
      (a, b) => b[1].reduce((s, l) => s + l.size, 0) - a[1].reduce((s, l) => s + l.size, 0),
    );
    const capped = spec.maxTiles === undefined ? ordered : ordered.slice(0, spec.maxTiles);
    return capped.map(([category, children]): TreemapBranch => ({ name: category, category, children }));
  })();

  const data: readonly TreemapNode[] = flat ? cappedFlat : branches;

  // Ordered category list for a stable, data-order colour assignment. Pass the
  // categories in their rendered order (largest first) so colours track the
  // visual order; the synthesized `Other` bucket gets a slot too.
  const categories = flat ? cappedFlat.map((leaf) => leaf.category) : branches.map((branch) => branch.category);
  // `:colors` overrides are keyed by raw category string; pin them over order.
  const scale = categoryColorScale(categories, spec.colors);

  const allSizes = rawLeaves.map((leaf) => leaf.size);
  const hints = numberHints(allSizes);
  const fmt = (n: number): string => formatNumber(n, hints);

  const getCsv = () =>
    csvFromRows(
      rawLeaves.map((leaf) => ({ label: leaf.name, value: leaf.size, group: leaf.category })),
      [
        { source: 'label', label: 'Label' },
        { source: 'value', label: 'Value' },
        ...(flat ? [] : [{ source: 'group', label: 'Group' }]),
      ],
    );
  const tileName = `treemap-${slugify(rawLeaves.at(0)?.name ?? 'treemap')}`;

  return (
    <TileFrame getCsv={getCsv} id={tileId} inspect={() => ({ spec, data })} name={tileName}>
      <div className={classes.treemapChart}>
        <ResponsiveContainer>
          <Treemap
            content={<TreemapTile fmt={fmt} leafDepth={flat ? 1 : 2} scale={scale} />}
            data={data as unknown as ComponentProps<typeof Treemap>['data']}
            dataKey="size"
            isAnimationActive={false}
            nameKey="name"
            stroke="var(--mantine-color-body)"
          />
        </ResponsiveContainer>
      </div>
    </TileFrame>
  );
}
